import React, { useEffect, useState } from 'react';
import { Mic, MicOff, Video, VideoOff, ScreenShare, ScreenShareOff, MessageSquare, Settings, PhoneOff, X, ChevronDown } from 'lucide-react';
import { useResponsive } from '../../../../contexts/ResponsiveContext';
import ScreenShareDropdown from '../ScreenShareDropdown';

interface DeviceOption {
  deviceId: string;
  label: string;
}

interface ControlBarProps {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  showMessageInput: boolean;
  isDeviceDropdownOpen: boolean;
  videoDevices: DeviceOption[];
  audioDevices: DeviceOption[];
  selectedVideoDevice: string;
  selectedAudioDevice: string;
  isScreenSharing: boolean;
  toggleMicrophone: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
  toggleMessageInput: () => void;
  toggleDeviceDropdown: () => void;
  handleEndInterview: () => void;
  handleDeviceChange: (deviceId: string, type: 'video' | 'audio') => void;
  showStopScreenShareConfirmation: boolean;
  setShowStopScreenShareConfirmation: (show: boolean) => void;
  confirmStopScreenShare: () => void;
  showMobileMessageSheet?: boolean;
  setShowMobileMessageSheet?: (show: boolean) => void;
}

const ControlBar: React.FC<ControlBarProps> = ({
  isMicEnabled,
  isCameraEnabled,
  showMessageInput,
  showStopScreenShareConfirmation,
  setShowStopScreenShareConfirmation,
  isDeviceDropdownOpen,
  videoDevices,
  audioDevices,
  selectedVideoDevice,
  selectedAudioDevice,
  isScreenSharing,
  toggleMicrophone,
  toggleCamera,
  toggleScreenShare,
  toggleMessageInput,
  toggleDeviceDropdown,
  handleEndInterview,
  handleDeviceChange,
  confirmStopScreenShare,
  showMobileMessageSheet = false,
  setShowMobileMessageSheet
}) => {
  const { isMobile: isMobileView } = useResponsive();
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false);

  // Check for very small screens (< 372px)
  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 372);
    };
    
    // Initial check
    checkScreenSize();
    
    // Add event listener for resize
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Handle mobile message button toggle
  const handleMobileMessageToggle = () => {
    if (setShowMobileMessageSheet) {
      setShowMobileMessageSheet(!showMobileMessageSheet);
    }
  };

  return (
    <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center px-3 md:px-2">
      <div className={`bg-surface-light dark:bg-surface-dark backdrop-blur-lg rounded-full px-${isVerySmallScreen ? '1' : '2'} py-${isVerySmallScreen ? '1' : '2'} sm:px-3 sm:py-3 border border-border-light dark:border-border-dark shadow-lg flex items-center justify-center ${isMobileView ? 'w-full max-w-full' : 'w-auto'} ${isVerySmallScreen ? 'space-x-1' : 'space-x-2'} sm:space-x-3 mx-auto`}>
        {/* Microphone control */}
        <button
          onClick={toggleMicrophone}
          className={`rounded-full ${isVerySmallScreen ? 'p-2' : 'p-3'} sm:p-3.5 transition-all duration-300 hover:scale-110 ${isMobileView ? 'flex-1 aspect-square' : ''} ${isVerySmallScreen ? 'w-10 h-10' : 'w-12 h-12'} ${isMicEnabled 
            ? 'bg-primary-light dark:bg-primary-dark text-white shadow-md shadow-primary-light/20 dark:shadow-primary-dark/20' 
            : 'bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark'}`}
          aria-label={isMicEnabled ? 'Turn off microphone' : 'Turn on microphone'}
        >
          {isMicEnabled ? <Mic className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} /> : <MicOff className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} />}
        </button>
        
        {/* Camera control */}
        <button
          onClick={toggleCamera}
          className={`rounded-full ${isVerySmallScreen ? 'p-2' : 'p-3'} sm:p-3.5 transition-all duration-300 hover:scale-110 ${isMobileView ? 'flex-1 aspect-square' : ''} ${isVerySmallScreen ? 'w-10 h-10' : 'w-12 h-12'} ${isCameraEnabled 
            ? 'bg-primary-light dark:bg-primary-dark text-white shadow-md shadow-primary-light/20 dark:shadow-primary-dark/20' 
            : 'bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark'}`}
          aria-label={isCameraEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {isCameraEnabled ? <Video className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} /> : <VideoOff className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} />}
        </button>
        
        {/* Conditional rendering: Screen Share button for desktop, Message button for mobile */}
        {isMobileView ? (
          /* Message button for mobile */
          <button
            onClick={handleMobileMessageToggle}
            className={`rounded-full ${isVerySmallScreen ? 'p-2' : 'p-3'} sm:p-3.5 transition-all duration-300 hover:scale-110 ${isMobileView ? 'flex-1 aspect-square' : ''} ${isVerySmallScreen ? 'w-10 h-10' : 'w-12 h-12'} ${showMobileMessageSheet 
              ? 'bg-primary-light dark:bg-primary-dark text-white shadow-md shadow-primary-light/20 dark:shadow-primary-dark/20' 
              : 'bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark'}`}
            aria-label={showMobileMessageSheet ? 'Close messages' : 'Open messages'}
            title="Send messages to AI interviewer"
          >
            <MessageSquare className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} />
          </button>
        ) : (
          /* Screen Share button for desktop */
        <button
          onClick={toggleScreenShare}
          className={`rounded-full ${isVerySmallScreen ? 'p-2' : 'p-3'} sm:p-3.5 transition-all duration-300 hover:scale-110 ${isMobileView ? 'flex-1 aspect-square' : ''} ${isVerySmallScreen ? 'w-10 h-10' : 'w-12 h-12'} ${isScreenSharing 
            ? 'bg-primary-light dark:bg-primary-dark text-white shadow-md shadow-primary-light/20 dark:shadow-primary-dark/20' 
            : 'bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark'}`}
          aria-label={isScreenSharing ? 'Stop screen sharing' : 'Start screen sharing'}
          title={isScreenSharing ? 'Stop screen sharing' : 'Share your screen (select which screen to share)'}
        >
          {isScreenSharing ? 
            <ScreenShareOff className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} /> : 
            <ScreenShare className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} />
          }
        </button>
        )}
        
        {/* Settings button with dropdown */}
        <div className={`relative ${isMobileView ? 'flex-1 aspect-square' : ''}`}>
          <button
            onClick={toggleDeviceDropdown}
            className={`rounded-full ${isVerySmallScreen ? 'p-2' : 'p-3'} sm:p-3.5 w-full bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark transition-all duration-300 hover:scale-110 ${isVerySmallScreen ? 'w-10 h-10' : 'w-12 h-12'} flex items-center justify-center`}
            aria-label="Device settings"
          >
            <Settings className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'}`} />
          </button>
          
          {showStopScreenShareConfirmation && (
            <ScreenShareDropdown
              isOpen={showStopScreenShareConfirmation}
              onClose={() => setShowStopScreenShareConfirmation(false)}
              onConfirm={() => {
                toggleScreenShare();
                confirmStopScreenShare();
              }}
            />
          )}
          {/* Device settings dropdown */}
          {isDeviceDropdownOpen && (
            <div className={`absolute bottom-full ${isMobileView ? 'right-0 mb-4' : 'left-1/2 -translate-x-1/2 mb-5'} ${isVerySmallScreen ? 'w-64' : 'w-72'} max-w-[90vw] bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark shadow-lg overflow-hidden z-50`}>
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">Device Settings</h4>
                  <button 
                    onClick={toggleDeviceDropdown}
                    className="p-1 rounded-full hover:bg-background-light/20 dark:hover:bg-background-dark/20"
                  >
                    <X className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                  </button>
                </div>
                
                <h4 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Camera</h4>
                <div className="relative mb-4">
                  <select 
                    value={selectedVideoDevice}
                    onChange={(e) => handleDeviceChange(e.target.value, 'video')}
                    className="w-full p-2 appearance-none bg-background-light/70 dark:bg-background-dark/70 rounded-lg border border-border-light dark:border-border-dark text-sm pr-8"
                  >
                    {videoDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                    {videoDevices.length === 0 && (
                      <option value="">No cameras found</option>
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                </div>
                
                <h4 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Microphone</h4>
                <div className="relative">
                  <select 
                    value={selectedAudioDevice}
                    onChange={(e) => handleDeviceChange(e.target.value, 'audio')}
                    className="w-full p-2 appearance-none bg-background-light/70 dark:bg-background-dark/70 rounded-lg border border-border-light dark:border-border-dark text-sm pr-8"
                  >
                    {audioDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                    {audioDevices.length === 0 && (
                      <option value="">No microphones found</option>
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* End Interview Button with confirmation modal */}
        <button
          onClick={handleEndInterview}
          className={`rounded-full ${isVerySmallScreen ? 'p-2' : 'p-3'} sm:p-3.5 bg-red-500 text-white shadow-md shadow-red-500/20 transition-all duration-300 hover:scale-110 ${isMobileView ? 'flex-1 aspect-square' : ''} ${isVerySmallScreen ? 'w-10 h-10' : 'w-12 h-12'}`}
          aria-label="End interview"
        >
          <PhoneOff className={`${isVerySmallScreen ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} />
        </button>
      </div>
    </div>
  );
};

export default ControlBar; 